import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithPopup,
  signInWithRedirect,
  GoogleAuthProvider
} from 'firebase/auth';
import { auth, db, provider } from '../lib/firebase';
import { doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { motion, AnimatePresence } from 'motion/react';
import { Mail, Lock, User, Phone, ArrowRight, X, Chrome, Check, Eye, EyeOff, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { generateStandReferralCode, isStandReferralCode } from '../lib/referral';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialMode?: 'login' | 'signup';
  role: 'stand' | 'festival';
  /** Message affiché en mode signup quand la modale s'ouvre depuis un lien de parrainage. */
  inviteNote?: string;
}

// Champs Firestore de parrainage exposant à poser à la création d'un compte stand :
//   - standReferralCode : code perso pour parrainer d'autres exposants.
//   - festinCredit : solde crédit (€), initialisé à 0.
//   - referredByStand : code du parrain capté via `?ref=STAND-XXXX` (si présent).
function buildStandReferralFields(role: 'stand' | 'festival'): Record<string, unknown> {
  if (role !== 'stand') return {};
  const fields: Record<string, unknown> = {
    standReferralCode: generateStandReferralCode(),
    festinCredit: 0,
  };
  const referredBy = (sessionStorage.getItem('festin_ref_stand') || '').trim().toUpperCase();
  if (isStandReferralCode(referredBy)) {
    fields.referredByStand = referredBy;
    fields.welcomeDiscountUsed = false;
  }
  sessionStorage.removeItem('festin_ref_stand');
  return fields;
}

export const AuthModal: React.FC<AuthModalProps> = ({ isOpen, onClose, initialMode = 'signup', role, inviteNote }) => {
  const [mode, setMode] = useState<'login' | 'signup' | 'forgot'>(initialMode);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [consentChecked, setConsentChecked] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleGoogleLogin = async () => {
    setLoading(true);
    // localStorage (pas sessionStorage) : doit survivre à la redirection complète
    // vers Google puis retour sur mobile (signInWithRedirect), où sessionStorage
    // n'est pas garanti de persister — c'était la cause du mauvais rôle assigné.
    localStorage.setItem('pendingSignupRole', role);

    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

    if (isMobile) {
      // Sur mobile, window.opener est null dans le nouvel onglet → signInWithPopup
      // ne peut pas renvoyer le résultat. On utilise la redirection pleine page.
      // Avec authDomain: festin.live, tout reste sur le même domaine → iOS OK.
      try {
        await signInWithRedirect(auth, provider);
      } catch {
        toast.error(t('auth.toastGoogleError'));
        setLoading(false);
      }
      return;
    }

    // Desktop : popup classique
    try {
      const result = await signInWithPopup(auth, provider);
      const user = result.user;

      const userRef = doc(db, 'users', user.uid);
      const userSnap = await getDoc(userRef);

      if (!userSnap.exists()) {
        const displayName = user.displayName || 'Utilisateur';
        // Parrainage promoteur (capté par /register).
        const referralCode = sessionStorage.getItem('festin_referral') || '';
        const referralLeadId = sessionStorage.getItem('festin_leadId') || '';
        // Parrainage exposant (stand → stand).
        const standRef = buildStandReferralFields(role);
        await setDoc(userRef, {
          name: displayName,
          email: user.email || '',
          role: role,
          ...(referralCode ? { affiliateCode: referralCode } : {}),
          ...(referralLeadId ? { leadId: referralLeadId } : {}),
          ...standRef,
          createdAt: serverTimestamp()
        });
        sessionStorage.setItem(`userRole_${user.uid}`, role);
        // Déclenche l'auto-ouverture du guide de démarrage (lu par App.tsx).
        localStorage.setItem('festin_just_signed_up', '1');

        // Sync Brevo + email de bienvenue (best-effort). Compte Google : l'adresse
        // est déjà vérifiée → le backend n'ajoute pas de CTA de vérification.
        const gParts = displayName.split(' ');
        try {
          const gToken = await user.getIdToken();
          fetch(`${import.meta.env.VITE_API_URL || ''}/api/sync-brevo-contact`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${gToken}` },
            body: JSON.stringify({ firstName: gParts[0] || '', lastName: gParts.slice(1).join(' ') || '', role, isNew: true }),
          }).catch(() => {});
        } catch { /* noop */ }

        // Tracking lead promoteur (best-effort).
        if (referralLeadId) {
          try {
            const token = await user.getIdToken();
            fetch(`${import.meta.env.VITE_API_URL || ''}/api/promoter-leads/track`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
              body: JSON.stringify({ leadId: referralLeadId }),
            }).catch(() => {});
          } catch { /* noop */ }
          sessionStorage.removeItem('festin_referral');
          sessionStorage.removeItem('festin_leadId');
        }
      } else {
        const existingRole = userSnap.data().role;
        if (!existingRole) {
          await setDoc(userRef, { role, updatedAt: serverTimestamp() }, { merge: true });
          sessionStorage.setItem(`userRole_${user.uid}`, role);
        } else {
          sessionStorage.setItem(`userRole_${user.uid}`, existingRole);
        }
      }

      localStorage.removeItem('pendingSignupRole');
      toast.success(t('auth.toastLoginSuccess'));
      onClose();
      navigate('/home');
    } catch (error: any) {
      const code = error?.code || '';
      if (code !== 'auth/popup-closed-by-user') {
        toast.error(t('auth.toastGoogleError'));
        console.error('[Google Auth]', code, error);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await sendPasswordResetEmail(auth, email);
      toast.success(t('auth.toastResetSent'));
      setMode('login');
    } catch (error: any) {
      if (error.code === 'auth/user-not-found') {
        toast.error(t('auth.toastNoAccount'));
      } else {
        toast.error(t('auth.toastSendError'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (mode === 'signup') {
        // 1. Create User
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        const user = userCredential.user;

        // 2. Add to Firestore
        const nameParts = name.trim().split(' ');
        const firstName = nameParts[0] || '';
        const lastName = nameParts.slice(1).join(' ') || '';
        // Parrainage promoteur : code + leadId captés par /register (sessionStorage).
        const referralCode = sessionStorage.getItem('festin_referral') || '';
        const referralLeadId = sessionStorage.getItem('festin_leadId') || '';
        // Parrainage exposant (stand → stand) : code perso + rattachement au parrain.
        const standRef = buildStandReferralFields(role);
        await setDoc(doc(db, 'users', user.uid), {
          name,
          email,
          role,
          ...(phone.trim() ? { phone: phone.trim() } : {}),
          ...(referralCode ? { affiliateCode: referralCode } : {}),
          ...(referralLeadId ? { leadId: referralLeadId } : {}),
          ...standRef,
          createdAt: serverTimestamp()
        });
        sessionStorage.setItem(`userRole_${user.uid}`, role);
        localStorage.setItem('userRole', role); // Backup persist for verify-email
        // Déclenche l'auto-ouverture du guide de démarrage (lu par App.tsx après /verify-email).
        localStorage.setItem('festin_just_signed_up', '1');

        // Sync Brevo + email UNIQUE bienvenue & vérification (brandé Festin,
        // envoyé par le backend). Firebase n'envoie plus son mail « noreply ».
        // Si le backend échoue, on retombe sur sendEmailVerification pour ne
        // jamais laisser l'utilisateur sans lien de vérification.
        try {
          const token = await user.getIdToken();
          const resp = await fetch(`${import.meta.env.VITE_API_URL || ''}/api/sync-brevo-contact`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ firstName, lastName, role, isNew: true }),
          });
          if (!resp.ok) throw new Error('welcome email failed');
        } catch {
          await sendEmailVerification(user).catch(() => {});
        }

        // Tracking lead promoteur : rattache l'inscription orga au lead (best-effort).
        if (referralLeadId) {
          try {
            const token = await user.getIdToken();
            fetch(`${import.meta.env.VITE_API_URL || ''}/api/promoter-leads/track`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
              body: JSON.stringify({ leadId: referralLeadId }),
            }).catch(() => {});
          } catch { /* noop */ }
          sessionStorage.removeItem('festin_referral');
          sessionStorage.removeItem('festin_leadId');
        }

        toast.success(t('auth.toastAccountCreated'));
        onClose();
        navigate('/verify-email');
      } else {
        // Login mode
        const userCredential = await signInWithEmailAndPassword(auth, email, password);
        const user = userCredential.user;

        // Fetch role if not in session
        const userSnap = await getDoc(doc(db, 'users', user.uid));
        if (userSnap.exists()) {
          const roleData = userSnap.data().role;
          sessionStorage.setItem(`userRole_${user.uid}`, roleData);
        }

        if (!user.emailVerified) {
          navigate('/verify-email');
        } else {
          navigate('/home');
        }
        onClose();
        toast.success(t('auth.toastWelcomeBack'));
      }
    } catch (error: any) {
      console.error(error);
      let msg = t('auth.toastAuthError');
      if (error.code === 'auth/email-already-in-use') msg = t('auth.toastEmailInUse');
      if (error.code === 'auth/operation-not-allowed') msg = t('auth.toastEmailAuthDisabled');
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-black/80 backdrop-blur-sm"
          />
          
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="relative w-full max-w-md bg-[#121212] border border-white/10 rounded-[32px] p-8 overflow-hidden shadow-2xl"
          >
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#ff5a00] to-transparent"></div>
            
            <button onClick={onClose} className="absolute top-6 right-6 text-gray-500 hover:text-white transition-colors">
              <X className="w-6 h-6" />
            </button>

            <div className="text-center mb-8">
              <h2 className="text-2xl font-black mb-2 tracking-tight">
                {mode === 'signup' ? t('auth.createAccount') : mode === 'forgot' ? t('auth.forgotPassword') : t('auth.login')}
              </h2>
              <p className="text-gray-400 text-sm">
                {mode === 'forgot' ? t('auth.resetLinkSent') : role === 'festival' ? t('auth.organizerSpace') : t('auth.exhibitorSpace')}
              </p>
              {inviteNote && mode === 'signup' && (
                <div className="inline-flex items-center gap-2 mt-4 bg-[#ff5a00]/10 border border-[#ff5a00]/25 text-[#ff5a00] text-xs font-semibold px-3 py-1.5 rounded-full">
                  <Sparkles className="w-3.5 h-3.5" /> {inviteNote}
                </div>
              )}
            </div>

            {mode === 'forgot' && (
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                  <input
                    type="email"
                    placeholder={t('auth.yourEmail') as string}
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full bg-white/5 border border-white/10 rounded-2xl pl-12 pr-4 py-4 focus:outline-none focus:ring-2 focus:ring-[#ff5a00] transition-all"
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#ff5a00] text-white font-black py-4 rounded-2xl hover:bg-[#ff6b1a] transition-all active:scale-95 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {loading ? t('auth.sending') : t('auth.sendLink')}
                  {!loading && <ArrowRight className="w-5 h-5" />}
                </button>
                <button type="button" onClick={() => setMode('login')} className="w-full text-center text-sm text-gray-500 hover:text-white transition-colors pt-1">
                  ← {t('auth.backToLogin')}
                </button>
              </form>
            )}

            {mode !== 'forgot' && (<>
            <form onSubmit={handleSubmit} className="space-y-4">
              {mode === 'signup' && (
                <div className="relative">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                  <input
                    type="text"
                    placeholder={t('auth.fullName') as string}
                    required
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full bg-white/5 border border-white/10 rounded-2xl pl-12 pr-4 py-4 focus:outline-none focus:ring-2 focus:ring-[#ff5a00] transition-all"
                  />
                </div>
              )}

              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <input
                  type="email"
                  placeholder={t('auth.email') as string}
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-white/5 border border-white/10 rounded-2xl pl-12 pr-4 py-4 focus:outline-none focus:ring-2 focus:ring-[#ff5a00] transition-all"
                />
              </div>

              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder={t('auth.password') as string}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-white/5 border border-white/10 rounded-2xl pl-12 pr-12 py-4 focus:outline-none focus:ring-2 focus:ring-[#ff5a00] transition-all"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(v => !v)}
                  aria-label={(showPassword ? t('auth.hidePassword') : t('auth.showPassword')) as string}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>

              {mode === 'signup' && role === 'festival' && (
                <div className="relative">
                  <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                  <input
                    type="tel"
                    placeholder={t('auth.phoneOptional') as string}
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="w-full bg-white/5 border border-white/10 rounded-2xl pl-12 pr-4 py-4 focus:outline-none focus:ring-2 focus:ring-[#ff5a00] transition-all"
                  />
                </div>
              )}

              {mode === 'login' && (
                <div className="text-right">
                  <button type="button" onClick={() => setMode('forgot')} className="text-xs text-gray-500 hover:text-[#ff5a00] transition-colors">
                    {t('auth.forgotPasswordLink')}
                  </button>
                </div>
              )}

              {mode === 'signup' && (
                <button
                  type="button"
                  onClick={() => setConsentChecked(v => !v)}
                  className="flex items-start gap-3 text-left w-full group"
                >
                  <div className={`mt-0.5 w-5 h-5 rounded-md border-2 shrink-0 flex items-center justify-center transition-all ${
                    consentChecked ? 'bg-[#ff5a00] border-[#ff5a00]' : 'border-white/20 group-hover:border-white/40'
                  }`}>
                    {consentChecked && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
                  </div>
                  <span className="text-xs text-gray-400 leading-relaxed">
                    {t('auth.acceptThe')}{' '}
                    <Link to="/terms" onClick={e => e.stopPropagation()} className="text-[#ff5a00] hover:underline font-semibold">
                      {t('auth.termsOfUse')}
                    </Link>{' '}
                    {t('auth.andThe')}{' '}
                    <Link to="/privacy" onClick={e => e.stopPropagation()} className="text-[#ff5a00] hover:underline font-semibold">
                      {t('auth.privacyPolicy')}
                    </Link>{' '}
                    {t('auth.ofFestin')} <span className="text-[#ff5a00]">*</span>
                  </span>
                </button>
              )}

              <button
                type="submit"
                disabled={loading || (mode === 'signup' && !consentChecked)}
                className="w-full bg-[#ff5a00] text-white font-black py-4 rounded-2xl hover:bg-[#ff6b1a] transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading ? t('auth.loading') : (
                  <>
                    {mode === 'signup' ? t('auth.signUp') : t('auth.login')}
                    <ArrowRight className="w-5 h-5" />
                  </>
                )}
              </button>
            </form>

            <div className="my-6 flex items-center gap-4">
              <div className="h-px flex-1 bg-white/10"></div>
              <span className="text-gray-500 text-xs font-bold">{t('auth.or')}</span>
              <div className="h-px flex-1 bg-white/10"></div>
            </div>

            <button
              onClick={handleGoogleLogin}
              disabled={loading}
              className="w-full bg-white/5 hover:bg-white/10 border border-white/10 text-white font-bold py-4 rounded-2xl transition-all flex items-center justify-center gap-3"
            >
              <Chrome className="w-5 h-5" />
              {t('auth.continueWithGoogle')}
            </button>

            <p className="mt-8 text-center text-sm text-gray-500">
              {mode === 'signup' ? t('auth.alreadyAccount') : t('auth.noAccount')}
              <button
                onClick={() => setMode(mode === 'signup' ? 'login' : 'signup')}
                className="ml-2 text-[#ff5a00] font-bold hover:underline"
              >
                {mode === 'signup' ? t('auth.login') : t('auth.signUp')}
              </button>
            </p>
            </>)}
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};
